import { useEffect } from "react";

/**
 * The launch intro's second half. index.html already shows the Reality Orb (identical to the
 * installed app's splash screen) and plays the first part in CSS before this bundle loads; this
 * component adopts that element, waits for the minimum time and the home data (capped at 3.5 s),
 * then flies the orb onto the header logo while the screen opens like a portal.
 * Tap anywhere to skip. Only the installed app's very first launch plays it ("full", ≤ 1.2 s); the website
 * never shows an intro and reduced motion skips it (index.html decides before this bundle loads).
 */
type Mode = "full" | "short" | "web" | "reduced";
interface IntroState {
  mode: Mode;
  start: number;
  lite: boolean;
  adopted?: boolean;
}

declare global {
  interface Window {
    __rsIntro?: IntroState;
    __rsHomeReady?: boolean;
  }
}

// Installed app, first launch only: the whole intro (wait + exit) stays within 1.2 s.
const MIN_MS: Record<Mode, number> = { full: 550, short: 400, web: 400, reduced: 200 };
const CAP_MS = 650;

function visibleLogo(): HTMLElement | null {
  const marks = Array.from(document.querySelectorAll<HTMLElement>("[data-logo-mark]"));
  return (
    marks.find((m) => {
      const r = m.getBoundingClientRect();
      return r.width > 0 && r.top >= 0 && r.top < 200;
    }) ?? null
  );
}

export function AppIntro() {
  useEffect(() => {
    const el = document.getElementById("rs-intro");
    const info = window.__rsIntro;
    if (!el || !info) return;
    info.adopted = true;
    let finished = false;
    let skip: (() => void) | null = null;
    const skipped = new Promise<void>((resolve) => (skip = resolve));
    const onTap = () => skip?.();
    el.addEventListener("pointerdown", onTap);

    const elapsed = performance.now() - info.start;
    const minWait = new Promise<void>((r) => window.setTimeout(r, Math.max(0, MIN_MS[info.mode] - elapsed)));
    const cap = new Promise<void>((r) => window.setTimeout(r, Math.max(0, CAP_MS - elapsed)));
    // The website version never waits for data (it must not hold back the first content); the
    // installed app opens onto loaded sections when it can, capped at 3.5 s.
    const ready = minWait;

    const remove = () => {
      el.remove();
      if (info.mode === "full") {
        const main = document.getElementById("main");
        if (main) {
          main.setAttribute("tabindex", "-1");
          main.focus({ preventScroll: true });
        }
      }
    };

    const open = async () => {
      if (finished) return;
      finished = true;
      // GSAP loads only for this one-time intro (installed app, first launch), never on the website.
      const { gsap } = await import("gsap");
      el.removeEventListener("pointerdown", onTap);
      if (info.mode === "reduced") {
        gsap.to(el, { opacity: 0, duration: 0.3, ease: "power1.out", onComplete: remove });
        return;
      }
      const orb = el.querySelector<SVGSVGElement>("#rs-intro-orb");
      const bg = el.querySelector<HTMLElement>(".rs-bg");
      const words = Array.from(el.querySelectorAll<HTMLElement>(".rs-word, .rs-tag"));
      // CSS animations (fill: forwards) outrank inline styles; hand the words over to GSAP.
      words.forEach((w) => {
        const shown = getComputedStyle(w).opacity;
        w.style.animation = "none";
        w.style.opacity = shown;
      });
      if (!orb || !bg) {
        remove();
        return;
      }
      const from = orb.getBoundingClientRect();
      const cx = from.left + from.width / 2;
      const cy = from.top + from.height / 2;
      bg.style.setProperty("--px", `${cx}px`);
      bg.style.setProperty("--py", `${cy + from.height * 0.12}px`); // the lock's doorway
      const reach = Math.hypot(Math.max(cx, window.innerWidth - cx), Math.max(cy, window.innerHeight - cy)) + 40;
      const target = visibleLogo();
      const tl = gsap.timeline({ onComplete: remove });
      tl.to(words, { opacity: 0, y: -6, duration: 0.18, ease: "power2.in" }, 0);
      if (target) {
        const to = target.getBoundingClientRect();
        // The orb drawing fills ~90% of its box; land it on the logo mark.
        const scale = (to.width * 1.05) / from.width;
        tl.to(
          orb,
          {
            x: to.left + to.width / 2 - cx,
            y: to.top + to.height / 2 - cy,
            scale,
            duration: 0.45,
            ease: "expo.inOut",
          },
          0.08,
        ).to(orb, { opacity: 0, duration: 0.14, ease: "none" }, 0.52);
      } else {
        tl.to(orb, { scale: 0.4, opacity: 0, duration: 0.4, ease: "power3.in" }, 0.08);
      }
      tl.fromTo(bg, { "--pr": "0px" }, { "--pr": `${reach}px`, duration: 0.45, ease: "power3.in" }, 0.08);
      const chrome = document.querySelectorAll<HTMLElement>("header.app-chrome, nav.app-chrome");
      if (chrome.length) {
        tl.from(
          chrome,
          {
            opacity: 0,
            y: (_i: number, t: HTMLElement) => (t.tagName === "NAV" ? 14 : -14),
            duration: 0.35,
            stagger: 0.06,
            ease: "power3.out",
            clearProps: "opacity,transform",
          },
          0.42,
        );
      }
    };

    void Promise.race([Promise.race([ready, cap]), skipped]).then(() => open());
    return () => {
      el.removeEventListener("pointerdown", onTap);
    };
  }, []);
  return null;
}
